import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Typography
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../services/api';
import { ADMIN_USERS, ROLES } from '../config/Config';

const UserCreationDialog = ({ open, onClose, onSuccess, tenants = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role_name: '',
    tenant: '',
    class_name: '',
    phone_number: '',
    address: ''
  });

  // Fetch roles from backend
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await api.get(ROLES);
      const rolesData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      
      // Filter to only show roles that admin can create
      const allowedRoles = ['STUDENT', 'TEACHER', 'PARENT', 'CHAMPIONS'];
      const filteredRoles = rolesData.filter(role => allowedRoles.includes(role.role_name));
      
      setRoles(filteredRoles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setRolesLoading(false);
    }
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Fetch roles when dialog opens
      fetchRoles();
      
      // Force reset all form fields to empty
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role_name: '',
        tenant: '',
        class_name: '',
        phone_number: '',
        address: ''
      });
      setError('');
      setSuccessMessage('');

      // Additional reset to ensure browser doesn't cache values
      setTimeout(() => {
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role_name: '',
          tenant: '',
          class_name: '',
          phone_number: '',
          address: ''
        });
      }, 100);
    }
  }, [open]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const payload = { ...formData };
      if (!payload.tenant) delete payload.tenant;
      const response = await api.post(ADMIN_USERS, payload);
      
      if (response.status === 201) {
        setSuccessMessage('User created successfully!');
        
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        
        // Close dialog after a brief delay to show success message
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      
      // Handle different types of errors
      if (error.response?.status === 400) {
        // Validation errors
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          // Handle field-specific errors
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => {
              if (Array.isArray(messages)) {
                return `${field}: ${messages.join(', ')}`;
              }
              return `${field}: ${messages}`;
            })
            .join('; ');
          setError(errorMessages);
        } else {
          setError(errorData || 'Validation failed');
        }
      } else if (error.response?.status === 403) {
        setError('You do not have permission to create users');
      } else if (error.response?.status === 401) {
        setError('Please log in again to continue');
      } else {
        setError('Failed to create user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccessMessage('');
    // Ensure form is completely reset when closing
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role_name: '',
      tenant: '',
      class_name: '',
      phone_number: '',
      address: ''
    });
    onClose();
  };

  const isFormValid = () => {
    return formData.email && 
           formData.password && 
           formData.first_name && 
           formData.last_name && 
           formData.role_name;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
             <form key={open ? 'open' : 'closed'} onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6">Create New User</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              name="first_name"
              label="First Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
            
            <TextField
              margin="dense"
              name="last_name"
              label="Last Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              name="email"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="off"
              inputProps={{ autoComplete: 'new-email' }}
            />
            
            <TextField
              margin="dense"
              name="password"
              label="Password"
              type="password"
              fullWidth
              variant="outlined"
              value={formData.password}
              onChange={handleChange}
              required
              helperText="Password must meet security requirements"
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
            />
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Role</InputLabel>
              <Select
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
                label="Role"
                disabled={rolesLoading}
              >
                <MenuItem value="">
                  <em>Select a role</em>
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.role_id} value={role.role_name}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
              {rolesLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Loading roles...
                  </Typography>
                </Box>
              )}
            </FormControl>
            
            <TextField
              margin="dense"
              name="class_name"
              label="Class Name (Optional)"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.class_name}
              onChange={handleChange}
              placeholder="e.g., Class 5A, Grade 10"
            />
          </Box>
          
          {tenants.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Tenant (Optional)</InputLabel>
                <Select
                  name="tenant"
                  value={formData.tenant}
                  onChange={handleChange}
                  label="Tenant (Optional)"
                >
                  <MenuItem value=""><em>Select tenant</em></MenuItem>
                  {tenants.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              name="phone_number"
              label="Phone Number (Optional)"
              type="tel"
              fullWidth
              variant="outlined"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
            />
            
            <TextField
              margin="dense"
              name="address"
              label="Address (Optional)"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main St, City, State"
            />
          </Box>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: 'info.light', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.main'
          }}>
            <Typography variant="body2" color="info.dark">
              <strong>Note:</strong> All new users will be automatically activated and verified. 
              They can log in immediately with the credentials you provide.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleClose}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={loading || !isFormValid()}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserCreationDialog;
